import { ghostBtnClass, mfgConfirmDialogClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { CogsPageCalcState } from "./useCogsPageMaterialsCalc";

type Props = {
  mode: "loading" | CogsPageCalcState["phase"];
  done: number;
  total: number;
  background?: boolean;
  onMinimize?: () => void;
};

export function ManufacturingPricingPageCalcDialog({
  mode,
  done,
  total,
  background = false,
  onMinimize,
}: Props) {
  const title =
    mode === "loading"
      ? "Loading COGS"
      : background
        ? "Filling later pages"
        : mode === "saving"
          ? "Opening saved totals"
          : "Calculating material costs";
  const body =
    mode === "loading"
      ? "Loading the saved catalog. After this, group, style, size, and page changes stay in this browser."
      : background
        ? `${done} products on later pages saved. This stays out of the way. Saved totals open immediately when you go there.`
        : mode === "saving"
          ? "Reading the totals that were just saved for this page."
          : `${done} of ${total} products on this page saved. Minimize this to keep working. Hover the clock if you want later pages filled in ahead of time.`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-labelledby="cogs-page-calc-title"
    >
      <div className={`max-w-md ${mfgConfirmDialogClass}`}>
        <h3 id="cogs-page-calc-title" className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          {title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400" aria-live="polite">
          {body}
        </p>
        {mode === "calculating" && total > 0 ? (
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-zinc-800">
            <div
              className="h-full rounded-full bg-indigo-600 transition-all"
              style={{ width: `${Math.min(100, Math.round((done / total) * 100))}%` }}
            />
          </div>
        ) : null}
        {onMinimize ? (
          <div className="flex justify-end">
            <button type="button" className={ghostBtnClass} onClick={onMinimize}>
              Minimize
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
